import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Flex,
  Form,
  Input,
  Select,
  Typography
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { Pencil } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  accountUserPartialUpdateMutation,
  type LanguageEnum,
  organizationsAccessPartialUpdateMutation,
  usersListQueryKey,
  usersPartialUpdateMutation,
  usersRetrieveQueryKey,
  type UserRetrieve
} from 'src/api/main'
import { AvatarWithInitials, PhoneInput, ResizableDrawer } from 'src/components'
import { UploadAvatar } from 'src/features/profile/components'
import { workspaceUsersListQueryKey } from 'src/features/users/hooks'
import type { WorkspaceUser } from 'src/features/users/types'
import { composeDisplayName } from 'src/features/users/utils'
import { useHasWorkspacePermissions, useSessionContext } from 'src/hooks'
import { allLangs } from 'src/locales'
import { paths } from 'src/router'
import { useShowApiErrors, WorkspacePermissionCode } from 'src/utils'

const { Text } = Typography

type Props = {
  open: boolean
  profile: UserRetrieve
  record?: WorkspaceUser
  workspaceId?: number
  photoSrc?: string
  onClose: () => void
}

type FormValues = {
  first_name: string
  last_name: string
  email: string
  phone?: PhoneFormValue
  birth_date?: Dayjs | null
  position?: string | null
  language?: LanguageEnum
  linkedin_url?: string
  about_me?: string
  job_title?: string | null
}

type PhoneFormValue =
  | string
  | number
  | {
      countryCode?: number | null
      areaCode?: string | null
      phoneNumber?: string | null
    }
  | null

const phoneFormValueToString = (value: PhoneFormValue | undefined) => {
  if (value == null) return ''
  if (typeof value === 'object') {
    return value.areaCode || value.phoneNumber
      ? [value.countryCode, value.areaCode, value.phoneNumber]
          .filter(Boolean)
          .join('')
          .trim()
      : ''
  }

  return String(value).trim()
}

export const AccountProfileEditDrawer = ({
  open,
  profile,
  record,
  workspaceId,
  photoSrc,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const { message } = AntdApp.useApp()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const showApiErrors = useShowApiErrors()
  const { user, activeWorkspaceId, removeUserData, updateUserData } =
    useSessionContext()
  // `job_title` lives on the workspace-access record, so editing it is a
  // membership change rather than a profile one.
  const mayManageMembers = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_MANAGE
  )
  const mayUploadAvatar = useHasWorkspacePermissions(
    WorkspacePermissionCode.ATTACHMENTS_UPLOAD
  )

  // The personal language can only be one the current workspace enables
  // (TS-CORE-027). Prefer the workspace being edited, then the active one.
  const languageOptions = useMemo(() => {
    const orgId = workspaceId ?? activeWorkspaceId
    const currentOrg =
      user?.organizations?.find(
        (organization) => Number(organization.id) === orgId
      ) ?? user?.organizations?.[0]
    const enabled = currentOrg?.enabled_languages
    const langs = enabled?.length
      ? allLangs.filter((lang) => enabled.includes(lang.value))
      : allLangs

    return langs.map((lang) => ({ value: lang.value, label: lang.label }))
  }, [user, activeWorkspaceId, workspaceId])

  const profileUpdate = useMutation(usersPartialUpdateMutation())
  const accountUpdate = useMutation(accountUserPartialUpdateMutation())
  const accessUpdate = useMutation(organizationsAccessPartialUpdateMutation())
  const isPending =
    profileUpdate.isPending || accountUpdate.isPending || accessUpdate.isPending

  const displayName = composeDisplayName(
    profile.first_name,
    profile.last_name,
    profile.email
  )

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      first_name: profile.first_name,
      last_name: profile.last_name,
      email: profile.email,
      phone: profile.phone ?? '',
      birth_date: profile.birth_date ? dayjs(profile.birth_date) : null,
      position: profile.position ?? '',
      language: profile.language,
      linkedin_url: profile.linkedin_url ?? '',
      about_me: profile.about_me ?? '',
      job_title: record?.job_title ?? ''
    })
  }, [form, open, profile, record])

  const submit = async (values: FormValues) => {
    const rawPhone = phoneFormValueToString(values.phone)
    const phone = rawPhone
      ? rawPhone.startsWith('+')
        ? rawPhone
        : `+${rawPhone}`
      : undefined
    const emailChanged = values.email !== profile.email
    // Hidden without `users.manage`, so its absent value must not be read as a
    // change that clears the stored title.
    const jobTitleChanged =
      mayManageMembers &&
      record != null &&
      (values.job_title ?? '') !== (record.job_title ?? '')

    try {
      // `job_title` lives on the workspace-access record, not the user — patch
      // it through the access endpoint before the profile fields.
      if (record != null && jobTitleChanged) {
        await accessUpdate.mutateAsync({
          path: { id: record.id },
          body: { job_title: values.job_title || null }
        })
      }

      const updatedProfile = await profileUpdate.mutateAsync({
        path: { id: profile.id },
        body: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone,
          position: values.position || null,
          language: values.language,
          linkedin_url: values.linkedin_url ?? '',
          about_me: values.about_me ?? '',
          birth_date: values.birth_date
            ? values.birth_date.format('YYYY-MM-DD')
            : null
        }
      })
      await accountUpdate.mutateAsync({ body: { email: values.email } })

      queryClient.setQueryData(
        usersRetrieveQueryKey({ path: { id: profile.id } }),
        updatedProfile
      )
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: usersRetrieveQueryKey({ path: { id: profile.id } })
        }),
        queryClient.invalidateQueries({ queryKey: usersListQueryKey() }),
        ...(workspaceId
          ? [
              queryClient.invalidateQueries({
                queryKey: workspaceUsersListQueryKey(workspaceId),
                refetchType: 'all'
              })
            ]
          : [])
      ])
      updateUserData()
      message.success(t('role_information_updated'))
      onClose()

      if (emailChanged) {
        removeUserData()
        navigate(paths.auth.login)
        message.success(t('enter_your_initial_email'))
      }
    } catch (error) {
      showApiErrors(error, form)
    }
  }

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      defaultWidth={520}
      storageKey="account-profile-edit-drawer-width"
      title={t('users.actions.edit')}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={isPending}>
            {t('users.editForm.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => form.submit()}
          >
            {t('users.editForm.save')}
          </Button>
        </Flex>
      }
    >
      <Form<FormValues> form={form} layout="vertical" onFinish={submit}>
        {mayUploadAvatar ? (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center" gap={12} wrap>
              <Flex vertical gap={2} style={{ minWidth: 0 }}>
                <Text strong>{t('account.avatar.card_title')}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('account.avatar.card_description')}
                </Text>
              </Flex>
              <UploadAvatar
                user={profile}
                userId={String(profile.id)}
                trigger={(openModal) => (
                  <Flex align="center" gap={10}>
                    <AvatarWithInitials
                      id={profile.id}
                      name={displayName}
                      size={48}
                      src={record?.user.photo?.file ?? photoSrc}
                    />
                    <Button icon={<Pencil size={14} />} onClick={openModal}>
                      {t('account.avatar.change')}
                    </Button>
                  </Flex>
                )}
              />
            </Flex>
          </Card>
        ) : null}
        <Form.Item
          name="first_name"
          label={t('users.detail.identity.firstName')}
          rules={[
            { required: true, message: t('users.editForm.required.firstName') }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          label={t('users.detail.identity.lastName')}
          rules={[
            { required: true, message: t('users.editForm.required.lastName') }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label={t('users.detail.identity.email')}
          rules={[
            { required: true, message: t('please_input') },
            { type: 'email' }
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={t('users.detail.identity.phone')}>
          <PhoneInput
            key={
              open
                ? `phone-${profile.id}-${profile.phone ?? ''}`
                : 'phone-closed'
            }
          />
        </Form.Item>
        <Form.Item
          name="birth_date"
          label={t('users.detail.identity.birthDate')}
        >
          <DatePicker
            placeholder={t('users.editForm.birthDatePlaceholder')}
            style={{ width: '100%' }}
            disabledDate={(current) => current && current.isAfter(dayjs())}
          />
        </Form.Item>
        <Form.Item name="position" label={t('users.detail.identity.position')}>
          <Input />
        </Form.Item>
        {record != null && mayManageMembers ? (
          <Form.Item
            name="job_title"
            label={t('users.detail.identity.jobTitle')}
          >
            <Input />
          </Form.Item>
        ) : null}
        <Form.Item name="language" label={t('users.detail.identity.language')}>
          <Select allowClear options={languageOptions} />
        </Form.Item>
        <Form.Item name="linkedin_url" label={t('linkedin_url')}>
          <Input placeholder="https://linkedin.com/in/..." />
        </Form.Item>
        <Form.Item name="about_me" label={t('users.editForm.aboutMe')}>
          <Input.TextArea rows={3} maxLength={1000} showCount />
        </Form.Item>
      </Form>
    </ResizableDrawer>
  )
}
